/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */
import eventBus from "@sd-utility/eventbus";

// Event constants for search functionality
export const OPEN_POPUP_EVENT = "search-bar/open-popup";
export const CLOSE_POPUP_EVENT = "search-bar/close-popup";

/**
 * Tracks search events using eventPulse (DataLayer + Celebrus) with UV fallback
 * @param eventName - DataLayer event name (e.g., 'search_button_click')
 * @param eventGroup - Celebrus event group (e.g., 'UX', 'Real Estate')
 * @param eventType - Celebrus event type (e.g., 'Search', 'Header')
 * @param label - Event label for tracking
 */
export const trackSearchEvent = (
  eventName: string,
  eventGroup: string,
  eventType: string,
  label: string,
) => {
  // Primary: Use eventPulse if available
  if (typeof window !== "undefined" && (window as any).eventPulse) {
    (window as any).eventPulse({
      eventName,
      eventGroup,
      eventType,
      eventData: { label },
    });
  }
  // Fallback: Use universal_variable for staged removal process
  else if (
    typeof window !== "undefined" &&
    (window as any).universal_variable
  ) {
    const uv = (window as any).universal_variable;
    if (uv.events) {
      uv.events.push({
        event_type: eventType,
        event_category: eventGroup,
        event_label: label,
        timestamp: new Date().toISOString(),
      });
    }
  }
  // Debug logging if neither is available
  else {
    console.warn("No analytics service available for event:", {
      eventName,
      eventGroup,
      eventType,
      label,
    });
  }
};

/**
 * Checks if an element is a descendant of the autosuggest modal
 * @param element - The element to check
 * @returns boolean indicating if the element is within the modal
 */
export const isDescendantOfModal = (element: EventTarget | null): boolean => {
  if (!element || !(element instanceof Element)) return false;

  let currentElement = element as Element;
  while (currentElement.parentElement) {
    const isModal = currentElement.parentElement.classList.contains(
      "search-autosuggest-fe__portal-container",
    );
    if (isModal) return true;
    currentElement = currentElement.parentElement;
  }
  return false;
};

/**
 * Fires the event to open the Autosuggest modal
 * @param e - Focus or click event
 * @returns Promise<boolean> - Whether the modal opened successfully
 */
export const handleOpenModal = async (
  e: React.FocusEvent | React.MouseEvent,
): Promise<boolean> => {
  // Prevents opening of modal after close when refocused
  if (
    e?.type === "focus" &&
    (!e.relatedTarget || isDescendantOfModal(e.relatedTarget))
  ) {
    return true;
  }

  // Check if there are subscribers for the OPEN_POPUP_EVENT
  const subs = (eventBus as any).subscribers.get(OPEN_POPUP_EVENT);
  if (!subs?.length) return false;

  eventBus.publish(OPEN_POPUP_EVENT, "open the popup");

  // Then check if autosuggest loaded successfully after timeout
  return new Promise((resolve) => {
    setTimeout(() => {
      const autosuggestInput = document.querySelector(
        ".search-autosuggest-fe__portal-container input",
      );

      if (!autosuggestInput) {
        eventBus.publish(CLOSE_POPUP_EVENT, "close the popup");
      }

      resolve(Boolean(autosuggestInput));
    }, 400);
  });
};

/**
 * Sanitises search input by replacing special characters
 * @param value - The input value to sanitise
 * @returns The sanitised string
 */
export const replaceSpecialChars = (value: string): string => {
  const replacements = [
    { pattern: /\W|_/g, replaceWith: "-" }, // Matches any character that is not a word character
    { pattern: /-{2,}/g, replaceWith: "-" }, // Matches any multiple hyphens
    { pattern: /^-|-$/g, replaceWith: "" }, // Matches first and or last character if hyphen
  ];

  return replacements.reduce(
    (term, regex) => term.replace(regex.pattern, regex.replaceWith),
    value,
  );
};

/**
 * Submits a search using the original URL pattern
 * @param searchTerm - The search term to submit
 */
export const submitSearch = (searchTerm: string): void => {
  if (searchTerm && searchTerm.trim().length > 0) {
    const sanitisedTerm = replaceSpecialChars(searchTerm.trim());
    // Use the original URL pattern from dcx-header-fe
    window.location.href = `/search/${sanitisedTerm}`;
  }
};

/**
 * Re-enables accessibility attributes on a search input element
 * @param inputRef - Reference to the input element
 */
export const reenableSearchInputAccessibility = (
  inputRef: React.RefObject<HTMLInputElement>,
): void => {
  if (inputRef.current) {
    inputRef.current.tabIndex = 0;
    inputRef.current.removeAttribute("aria-hidden");
    inputRef.current.removeAttribute("aria-disabled");
  }
};

/**
 * Disables accessibility attributes on a search input element
 * @param inputRef - Reference to the input element
 */
export const disableSearchInputAccessibility = (
  inputRef: React.RefObject<HTMLInputElement>,
): void => {
  if (inputRef.current) {
    inputRef.current.tabIndex = -1;
    inputRef.current.setAttribute("aria-hidden", "true");
    inputRef.current.setAttribute("aria-disabled", "true");
  }
};

/**
 * Creates a transparent overlay element for fallback search
 * @returns HTMLDivElement with fallback overlay styles
 */
export const createFallbackOverlay = (): HTMLDivElement => {
  const overlay = document.createElement("div");
  overlay.style.position = "fixed";
  overlay.style.top = "0";
  overlay.style.left = "0";
  overlay.style.width = "100%";
  overlay.style.height = "100%";
  overlay.style.background = "rgba(0, 0, 0, 0.45)";
  overlay.style.zIndex = "4";
  overlay.style.pointerEvents = "none";
  overlay.style.transition = "opacity 200ms";
  overlay.style.willChange = "opacity";
  overlay.style.opacity = "1";
  return overlay;
};
